import { CategoryMap, useCategoryMap } from '@common/categoryMap';
import { PATH_ROOTS } from '@common/constants';
import { useSpecs } from '@common/graph/gem-specs';
import { getConnectionLabels, getNodeProperties } from '@common/ide/graph-utils';
import { useBackendLanguage } from '@common/language';
import { getSubgraphVirtualNodeIds } from '@common/subgraph/graph';
import { GemSpec } from '@common/types';
import { useOnLayout } from '@common/utils/graph/layout';
import { getNodeIcon } from '@common/utils/graph/node';
import { useLSPMethod } from '@LSP/base/hook';
import { LSP } from '@LSP/base/types';
import { BackEndCodeLanguageType } from '@prophecy/graphql-types/enums';
import { EdgeData, SimplePipelineNodeData } from '@prophecy/ui-v3/Graph/types';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { getProcessesPath, isSubGraphOrMetaGem } from 'frontend/core/src/Parser/bindings';
import { useEffect } from 'react';
import { useStore } from 'react-redux';
import { Edge, Node } from 'reactflow';

import { useMixPanel } from '../../../context/mixpanel/context';
import { getLineageStyle } from '../graph-utils';
import { LineageConnection, LineageGraph, LineageState } from '../types';

export function useProcessSelect(graphPath: string) {
  const { method: propertiesDidAction } = useLSPMethod(LSP.Method.propertiesDidAction);
  return usePersistentCallback(function onElementClick(id: string) {
    propertiesDidAction({
      actionName: 'ProcessOpen',
      elementId: '-1',
      graphPath,
      params: {
        processId: id
      }
    });
  });
}

export const useGraph = (
  graphRoot: LineageGraph,
  graph: LineageGraph,
  graphPath: string,
  setNodes: React.Dispatch<React.SetStateAction<Node<SimplePipelineNodeData>[]>>,
  setEdges: React.Dispatch<React.SetStateAction<Edge<EdgeData>[]>>
) => {
  const store = useStore<LineageState>();

  const { track } = useMixPanel();

  const backendLanguage = useBackendLanguage();

  const categoryMap = useCategoryMap();
  const specs = useSpecs();
  const onLayout = useOnLayout(graph, (processes) => {
    store.dispatch({
      type: LSP.Method.propertiesDidChange,
      payload: { property: getProcessesPath(PATH_ROOTS.LINEAGE), value: processes }
    });
  });

  useEffect(() => {
    const edges = graph ? workflowToEdges(specs, graph, graphRoot, graphPath) : [];
    setEdges(edges);
  }, [graph, graphRoot, graphPath, setEdges, specs]);
  useEffect(() => {
    const nodes = graph ? workflowToNodes(specs, graph, categoryMap, backendLanguage) : [];
    setNodes(nodes);
  }, [graph, categoryMap, setNodes, track, specs, backendLanguage]);

  return { onLayout };
};

function workflowToEdges(specs: GemSpec[], graph: LineageGraph, workflow: LineageGraph, graphPath: string) {
  const edges = graph.connections.map((conn) => connectionsToEdge(conn, graph, workflow, graphPath));

  if (isSubGraphOrMetaGem(graph, specs)) {
    const { inNodeId, outNodeId } = getSubgraphVirtualNodeIds(graph.id);
    return edges.map((el) => {
      if (el.source === graph.id) {
        return {
          ...el,
          source: inNodeId,
          data: { ...el.data }
        } as Edge<EdgeData>;
      } else if (el.target === graph.id) {
        return { ...el, target: outNodeId };
      } else {
        return el;
      }
    });
  } else {
    return edges;
  }
}

function workflowToNodes(
  specs: GemSpec[],
  graph: LineageGraph,
  categoryMap: CategoryMap,
  backendLanguage: BackEndCodeLanguageType
): Node<SimplePipelineNodeData>[] {
  const processes = Object.values(graph.processes);
  return processes.map((process) => {
    const id = process.id;

    const { gemTypeLabel, position, label, comment, sourceHandles, targetHandles } = getNodeProperties({
      gemSpecs: specs,
      process,
      usedPorts: {},

      backendLanguage
    });
    const gemIcon = getNodeIcon({
      gemSpecs: specs,
      process,
      categoryMap
    });
    return {
      id: id,
      type: 'simpleGemNode',
      position,
      data: {
        allowToolSection: false,
        onOpenClick: (_event: React.MouseEvent) => {},
        useNodeState: () => ({ state: 'default', tooltip: null, status: process.lineage.status }),
        label,
        gemType: gemTypeLabel,
        comment,
        gemIcon,
        sourceHandles,
        targetHandles
      }
    };
  });
}
function connectionsToEdge(
  graphConnection: LineageConnection,
  graph: LineageGraph,
  rootGraph: LineageGraph,
  graphPath: string
): Edge<EdgeData> {
  const { source, sourcePort: sourceHandle, target, targetPort: targetHandle } = graphConnection;
  const { sourceLabel, targetLabel } = getConnectionLabels(graphConnection, {
    graph,
    rootGraph,
    graphPath,
    rootPath: PATH_ROOTS.WORKFLOW
  });

  const edge = {
    id: graphConnection.id,
    source,
    sourceHandle,
    target,
    targetHandle,
    type: 'default',
    style: getLineageStyle(graphConnection.lineage.status, false),
    data: {
      readOnly: true,
      sourceLabel,
      targetLabel,
      points: []
    }
  } as Edge<EdgeData>;
  return edge;
}
